import { useEffect, useRef } from 'react';
import type { Role } from '@lichess-org/chessground/types';

const PIECES: Array<{ role: Role; label: string }> = [
  { role: 'queen', label: 'Queen' },
  { role: 'rook', label: 'Rook' },
  { role: 'bishop', label: 'Bishop' },
  { role: 'knight', label: 'Knight' },
];

export interface PromotionModalProps {
  open: boolean;
  /** Legal promotion candidates supplied by the server. */
  candidates: string[];
  onSelect: (role: Role) => void;
  onCancel: () => void;
}

/**
 * Accessible promotion picker (§6.2). A modal dialog with focus moved in on
 * open, Escape to cancel, a simple focus trap, and focus restored to the
 * trigger on close. Server legality remains authoritative — we only choose
 * among the server's promotion candidates.
 */
export function PromotionModal({
  open,
  onSelect,
  onCancel,
}: PromotionModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const firstButton = dialogRef.current?.querySelector<HTMLButtonElement>('button');
    firstButton?.focus();
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused !== null && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [open, onCancel]);

  if (!open) return null;

  function trapFocus(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="modal-backdrop" data-testid="promotion-modal">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="promotion-title"
        aria-describedby="promotion-description"
        onKeyDown={trapFocus}
        className="modal"
      >
        <h2 id="promotion-title">Promote pawn</h2>
        <p id="promotion-description">Choose a piece to promote to.</p>
        <div className="promotion-choices">
          {PIECES.map((piece) => (
            <button
              key={piece.role}
              type="button"
              onClick={() => onSelect(piece.role)}
              aria-label={`Promote to ${piece.label.toLowerCase()}`}
            >
              {piece.label}
            </button>
          ))}
        </div>
        <button type="button" className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
